import { createContext, useEffect } from 'react';
import { ReactNode, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import { exchangeAndReturnService } from 'services';
import { useConsignmentsDetails } from 'hooks';
import { ConsignmentDetails, ReturnDetails } from 'types';

export type SelectedCard = {
  type: 'return' | 'toReturn';
  protocol: string;
  consignmentCode: string;
} | null;

interface ReturnDetailsData {
  selectedCard: SelectedCard;
  setSelectedCard: React.Dispatch<React.SetStateAction<SelectedCard>>;
  returnDetails: ReturnDetails | undefined;
  validatingReturnDetails: boolean;
  mutateReturnDetails: KeyedMutator<ReturnDetails>;
  consignmentDetails: ConsignmentDetails | undefined;
  validatingConsignmentsDetails: boolean;
  mutateConsignmentDetails: KeyedMutator<ConsignmentDetails>;
  renewPostCodeExpirationDate: () => Promise<void>;
}

export const ReturnDetailsContext = createContext<ReturnDetailsData>(
  {} as ReturnDetailsData
);

interface ComponentProps {
  children: ReactNode;
}

export function ReturnDetailsProvider({ children }: ComponentProps) {
  const [selectedCard, setSelectedCard] = useState<SelectedCard | null>(null);
  const [contextReturnDetails, setContextReturnDetails] = useState<
    ReturnDetails | undefined
  >(undefined);

  const protocol = selectedCard?.protocol || '';

  const {
    data: returnDetails,
    isValidating: validatingReturnDetails,
    mutate: mutateReturnDetails,
  } = useSWR(
    selectedCard?.type === 'return' ? `return-details-${protocol}` : null,
    () => exchangeAndReturnService.getDetails({ protocol }),
    { revalidateOnFocus: false }
  );

  const {
    data: consignmentDetails,
    isValidating: validatingConsignmentsDetails,
    mutate: mutateConsignmentDetails,
  } = useConsignmentsDetails(selectedCard?.consignmentCode || '');

  async function renewPostCodeExpirationDate() {
    if (!returnDetails) return;

    const { protocol } = returnDetails;
    const currentReturnDetails = returnDetails;

    const updatedPostOffice = await exchangeAndReturnService.postPostCode({
      protocol,
    });

    const updatedReturnDetails: ReturnDetails = {
      ...currentReturnDetails,
      postOffice: updatedPostOffice,
    };

    setContextReturnDetails(updatedReturnDetails);
  }

  useEffect(() => {
    setContextReturnDetails(returnDetails);
  }, [returnDetails]);

  return (
    <ReturnDetailsContext.Provider
      value={{
        selectedCard,
        setSelectedCard,
        returnDetails: contextReturnDetails,
        validatingReturnDetails,
        mutateReturnDetails,
        consignmentDetails,
        validatingConsignmentsDetails,
        mutateConsignmentDetails,
        renewPostCodeExpirationDate,
      }}
    >
      {children}
    </ReturnDetailsContext.Provider>
  );
}
